import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp, Trophy, Lightbulb, Megaphone, Globe2, Scale } from "lucide-react";

const fmtEur = (n) => (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const fmtInt = (n) => (Number(n) || 0).toLocaleString("fr-FR");

export const YieldComparisonSection = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d = days) => {
    setLoading(true);
    api.get(`/admin/adsense/yield?days=${d}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  const winnerCfg = useMemo(() => ({
    voamada: { label: "Voamada Ads gagne", color: "from-brand-orange to-brand-red", icon: Megaphone },
    adsense: { label: "AdSense gagne",     color: "from-blue-500 to-indigo-500",   icon: Globe2 },
    tie:     { label: "Match nul",          color: "from-white/20 to-white/10",     icon: Scale   },
  }[data?.winner] || { label: "—", color: "from-white/10 to-white/5", icon: Scale }), [data]);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-5" data-testid="yield-comparison-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display text-lg font-black inline-flex items-center gap-2"><Trophy size={16} className="text-brand-yellow" /> Yield — Voamada vs AdSense</h3>
          <p className="text-xs text-white/55 mt-0.5">Compare le rendement de tes sponsors directs (CPC/CPM/CPA) vs Google AdSense, par emplacement.</p>
        </div>
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="rounded-lg bg-white/[0.04] border border-white/10 px-2 py-1.5 text-xs" data-testid="yield-period">
          <option value="7">7 jours</option>
          <option value="30">30 jours</option>
          <option value="90">90 jours</option>
        </select>
      </div>

      {loading && <p className="text-white/50 text-sm">Calcul du rendement…</p>}

      {!loading && data && (
        <>
          {/* HERO : Verdict global */}
          <div className={`rounded-2xl bg-gradient-to-br ${winnerCfg.color} p-1`}>
            <div className="rounded-2xl bg-[#15100D] p-5 grid md:grid-cols-3 gap-4 items-center">
              <div className="md:col-span-1 text-center md:text-left">
                <p className="text-[11px] uppercase tracking-widest text-white/45 font-bold mb-1">Verdict {days}j</p>
                <p className="font-display text-2xl font-black inline-flex items-center gap-2">
                  <winnerCfg.icon size={20} /> {winnerCfg.label}
                </p>
                {data.delta_pct > 0 && data.winner !== "tie" && (
                  <p className="text-xs text-white/55 mt-1">+{data.delta_pct}% de revenus vs l'autre canal</p>
                )}
              </div>
              <Versus
                left={{ label: "Voamada Ads", icon: Megaphone, totals: data.voamada, colorClass: "text-brand-orange" }}
                right={{ label: "AdSense", icon: Globe2, totals: data.adsense, colorClass: "text-blue-300" }}
                winner={data.winner}
              />
            </div>
          </div>

          {/* PER PLACEMENT */}
          <div>
            <p className="text-[11px] uppercase tracking-widest text-white/45 font-bold mb-3">Par emplacement</p>
            <div className="space-y-2">
              {data.per_placement.map((p) => (
                <PlacementRow key={p.placement} row={p} />
              ))}
            </div>
          </div>

          {/* RECOMMANDATIONS */}
          {data.recommendations?.length > 0 && (
            <div className="rounded-2xl border border-brand-yellow/20 bg-brand-yellow/5 p-4">
              <p className="text-xs uppercase tracking-widest text-brand-yellow font-bold mb-3 inline-flex items-center gap-1.5"><Lightbulb size={12} /> Recommandations</p>
              <ul className="space-y-2">
                {data.recommendations.map((r, i) => (
                  <li key={i} className="text-sm text-white/80" data-testid={`yield-reco-${i}`}>
                    <strong className="text-brand-yellow">{r.placement} :</strong> {r.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!data.adsense_connected && (
            <p className="text-xs text-white/45 italic">
              ⓘ AdSense non connecté — la comparaison est basée uniquement sur Voamada Ads. Connecte AdSense pour activer le yield management complet.
            </p>
          )}
        </>
      )}
    </div>
  );
};

const Versus = ({ left, right, winner }) => {
  const total = (left.totals.earnings_eur || 0) + (right.totals.earnings_eur || 0);
  const leftPct  = total > 0 ? Math.round(100 * (left.totals.earnings_eur || 0) / total) : 50;
  const rightPct = 100 - leftPct;

  return (
    <div className="md:col-span-2">
      {/* Bar versus */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs font-bold ${left.colorClass} flex-1 text-right`}>{leftPct}%</span>
        <div className="relative w-2/3 h-3 rounded-full overflow-hidden bg-black/40">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-red to-brand-orange transition-all" style={{ width: `${leftPct}%` }} />
          <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-blue-400 to-indigo-500 transition-all" style={{ width: `${rightPct}%` }} />
        </div>
        <span className={`text-xs font-bold ${right.colorClass} flex-1`}>{rightPct}%</span>
      </div>
      {/* Twin cards */}
      <div className="grid grid-cols-2 gap-3">
        {[left, right].map((side) => {
          const isWinner = (winner === "voamada" && side === left) || (winner === "adsense" && side === right);
          return (
            <div key={side.label} className={`rounded-xl border p-3 ${isWinner ? "border-brand-yellow/40 bg-brand-yellow/5" : "border-white/10 bg-black/20"}`} data-testid={`yield-card-${side.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/50 font-bold">
                  <side.icon size={11} /> {side.label}
                </span>
                {isWinner && <Trophy size={12} className="text-brand-yellow" />}
              </div>
              <p className="font-display text-2xl font-black tabular-nums">{fmtEur(side.totals.earnings_eur)}</p>
              <div className="grid grid-cols-3 gap-1 mt-2 text-[10px] text-white/50">
                <span><strong className="text-white/80">{fmtInt(side.totals.impressions)}</strong> imp</span>
                <span><strong className="text-white/80">{fmtInt(side.totals.clicks)}</strong> clk</span>
                <span><strong className="text-white/80">{fmtEur(side.totals.ecpm)}</strong> eCPM</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PlacementRow = ({ row }) => {
  const total = (row.voamada.earnings_eur || 0) + (row.adsense.earnings_eur || 0);
  const lpct = total > 0 ? Math.round(100 * (row.voamada.earnings_eur || 0) / total) : 50;
  const winColor = {
    voamada: "border-brand-orange/40 bg-brand-orange/5",
    adsense: "border-blue-400/40 bg-blue-500/5",
    tie:     "border-white/10 bg-white/[0.02]",
  }[row.winner];

  return (
    <div className={`rounded-xl border ${winColor} p-3 grid md:grid-cols-[180px_1fr_180px] items-center gap-3`} data-testid={`placement-${row.placement}`}>
      <div className="text-sm font-bold">{row.placement_label}</div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] tabular-nums text-brand-orange font-bold w-20 text-right">{fmtEur(row.voamada.earnings_eur)}</span>
        <div className="relative flex-1 h-2 rounded-full overflow-hidden bg-black/40">
          {total > 0 && (
            <>
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-red to-brand-orange" style={{ width: `${lpct}%` }} />
              <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-blue-400 to-indigo-500" style={{ width: `${100 - lpct}%` }} />
            </>
          )}
        </div>
        <span className="text-[11px] tabular-nums text-blue-300 font-bold w-20">{fmtEur(row.adsense.earnings_eur)}</span>
      </div>
      <div className="text-right">
        {row.winner === "voamada" && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-brand-orange/20 text-brand-orange border border-brand-orange/30 font-bold uppercase tracking-widest"><Trophy size={9} /> Sponsors</span>}
        {row.winner === "adsense" && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30 font-bold uppercase tracking-widest"><Trophy size={9} /> AdSense</span>}
        {row.winner === "tie" && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-white/10 text-white/55 font-bold uppercase tracking-widest">Égalité</span>}
      </div>
    </div>
  );
};

export default YieldComparisonSection;
